import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Users as UsersIcon, X, Check, Loader2 } from "lucide-react";
import useConfirm from "@/hooks/useConfirm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * TeamManagement — Settings → Team → Teams panel.
 *
 * super_admin / crm_admin may create + delete teams. sales_manager may patch
 * existing teams + manage membership. Every write persists through the
 * /api/teams endpoints — no local-only state in real mode.
 */
export default function TeamManagement({ role, users = [] }) {
  const canAdmin = role === "super_admin" || role === "crm_admin";
  const canManage = canAdmin || role === "sales_manager";
  const confirm = useConfirm();

  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [creating, setCreating] = useState(false);
  const [addingForTeam, setAddingForTeam] = useState(null);
  const [addingUserId, setAddingUserId] = useState("");

  const token = useMemo(() => localStorage.getItem("token"), []);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/teams`, { headers: { Authorization: `Bearer ${token}` } });
      setTeams(r.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load teams");
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- reload is a closure over fresh state; we only want it on mount
  useEffect(() => { reload(); }, []);

  const createTeam = async (e) => {
    e?.preventDefault();
    if (!newName.trim()) { toast.error("Team name is required"); return; }
    setCreating(true);
    try {
      await axios.post(`${API}/teams`,
        { name: newName.trim(), description: newDesc.trim() || null },
        { headers: { Authorization: `Bearer ${token}` } });
      setNewName(""); setNewDesc("");
      toast.success("Team created");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create team");
    } finally { setCreating(false); }
  };

  const deleteTeam = async (id, name) => {
    const ok = await confirm({
      title: `Delete team "${name}"?`,
      body: "Leads currently linked to this team will be unassigned. Members of the team will lose their team affiliation. This cannot be undone.",
      confirmLabel: "Delete team",
      confirmTone: "danger",
      testid: "team-delete-confirm",
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/teams/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Team deleted");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete team");
    }
  };

  const patchTeam = async (id, patch) => {
    try {
      await axios.patch(`${API}/teams/${id}`, patch, { headers: { Authorization: `Bearer ${token}` } });
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update team");
    }
  };

  const addMember = async (teamId) => {
    if (!addingUserId) return;
    try {
      await axios.post(`${API}/teams/${teamId}/members`,
        { user_id: addingUserId },
        { headers: { Authorization: `Bearer ${token}` } });
      setAddingForTeam(null); setAddingUserId("");
      toast.success("Member added");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add member");
    }
  };

  const removeMember = async (teamId, userId) => {
    try {
      await axios.delete(`${API}/teams/${teamId}/members/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Member removed");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to remove member");
    }
  };

  const userLabel = (uid) => {
    const u = users.find((x) => x.id === uid);
    return u ? (u.full_name || u.email || uid) : uid;
  };

  return (
    <div data-testid="team-management" style={{ fontSize: 12 }}>
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Teams</div>
      <div style={{ color: "var(--text-tertiary)", marginBottom: 20 }}>
        Organise users into teams to scope lead ownership, dialer campaigns, and reporting.
      </div>

      {canAdmin && (
        <form onSubmit={createTeam} data-testid="team-create-form"
          style={{ padding: 16, background: "var(--bg-subtle, #F8F9FA)", borderRadius: 8, marginBottom: 20 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr auto", gap: 8 }}>
            <input data-testid="team-create-name" placeholder="Team name *" value={newName}
              onChange={(e) => setNewName(e.target.value)} className="input" style={{ fontSize: 12 }} />
            <input data-testid="team-create-desc" placeholder="Description (optional)" value={newDesc}
              onChange={(e) => setNewDesc(e.target.value)} className="input" style={{ fontSize: 12 }} />
            <button type="submit" className="btn btn-primary" data-testid="team-create-submit" disabled={creating}
              style={{ fontSize: 12, whiteSpace: "nowrap" }}>
              {creating ? <Loader2 className="animate-spin" style={{ width: 12, height: 12 }} /> : <Plus style={{ width: 12, height: 12 }} />}
              Create team
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div data-testid="team-list-loading" style={{ textAlign: "center", padding: 32 }}>
          <Loader2 className="animate-spin" style={{ width: 18, height: 18, color: "var(--text-tertiary)" }} />
        </div>
      ) : teams.length === 0 ? (
        <div data-testid="team-list-empty" style={{ padding: 24, textAlign: "center", color: "var(--text-tertiary)" }}>
          No teams yet. {canAdmin && "Create one above to get started."}
        </div>
      ) : (
        <div data-testid="team-list" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {teams.map((t) => (
            <div key={t.id} data-testid={`team-row-${t.id}`} className="card"
              style={{ padding: 14, border: "1px solid var(--border-light)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <UsersIcon style={{ width: 14, height: 14, color: "var(--blue)" }} />
                    <span style={{ fontSize: 13, fontWeight: 600 }} data-testid={`team-row-${t.id}-name`}>{t.name}</span>
                    {!t.is_active && <span style={{ fontSize: 10, padding: "2px 6px", background: "#FFEBEB", color: "#A32D2D", borderRadius: 4 }}>inactive</span>}
                  </div>
                  {t.description && <div style={{ fontSize: 11, color: "var(--text-tertiary)", marginTop: 4 }}>{t.description}</div>}
                  <div style={{ fontSize: 10, color: "var(--text-tertiary)", marginTop: 6 }}>
                    {(t.member_user_ids || []).length} member{(t.member_user_ids || []).length === 1 ? "" : "s"}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  {canManage && (
                    <button onClick={() => patchTeam(t.id, { is_active: !t.is_active })} className="btn btn-ghost"
                      data-testid={`team-row-${t.id}-toggle-active`} style={{ fontSize: 11 }}>
                      {t.is_active ? "Deactivate" : "Activate"}
                    </button>
                  )}
                  {canAdmin && (
                    <button onClick={() => deleteTeam(t.id, t.name)} className="btn btn-ghost"
                      data-testid={`team-row-${t.id}-delete`} style={{ fontSize: 11, color: "#A32D2D" }}>
                      <Trash2 style={{ width: 12, height: 12 }} />
                    </button>
                  )}
                </div>
              </div>

              {/* Member roster */}
              <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 6 }}
                data-testid={`team-row-${t.id}-members`}>
                {(t.member_user_ids || []).map((uid) => (
                  <span key={uid} data-testid={`team-row-${t.id}-member-${uid}`}
                    style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 8px", background: "#EDF3FF", color: "#1B3D8A", borderRadius: 999, fontSize: 11 }}>
                    {userLabel(uid)}
                    {canManage && (
                      <button onClick={() => removeMember(t.id, uid)} aria-label="Remove member"
                        data-testid={`team-row-${t.id}-member-${uid}-remove`}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "#1B3D8A", padding: 0, display: "flex" }}>
                        <X style={{ width: 11, height: 11 }} />
                      </button>
                    )}
                  </span>
                ))}
                {canManage && (
                  <>
                    {addingForTeam === t.id ? (
                      <span style={{ display: "inline-flex", gap: 4 }}>
                        <select value={addingUserId} onChange={(e) => setAddingUserId(e.target.value)} className="input"
                          data-testid={`team-row-${t.id}-add-select`} style={{ fontSize: 11, padding: "3px 6px" }}>
                          <option value="">Select user…</option>
                          {users.filter((u) => !(t.member_user_ids || []).includes(u.id)).map((u) => (
                            <option key={u.id} value={u.id}>{u.full_name || u.email || u.id}</option>
                          ))}
                        </select>
                        <button onClick={() => addMember(t.id)} data-testid={`team-row-${t.id}-add-confirm`}
                          className="btn btn-primary" style={{ fontSize: 10, padding: "3px 8px" }} disabled={!addingUserId}>
                          <Check style={{ width: 11, height: 11 }} />
                        </button>
                        <button onClick={() => { setAddingForTeam(null); setAddingUserId(""); }}
                          className="btn btn-ghost" style={{ fontSize: 10, padding: "3px 8px" }}>
                          <X style={{ width: 11, height: 11 }} />
                        </button>
                      </span>
                    ) : (
                      <button onClick={() => { setAddingForTeam(t.id); setAddingUserId(""); }}
                        data-testid={`team-row-${t.id}-add-member`} className="btn btn-ghost"
                        style={{ fontSize: 11, padding: "3px 8px", border: "1px dashed var(--border)" }}>
                        <Plus style={{ width: 11, height: 11 }} /> Add member
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
